import { useState } from 'react';
import { MagnifyingGlassIcon, QrCodeIcon } from '@heroicons/react/24/outline';
import { useStudentRecord } from '../hooks/useStudentRecord';
import { SCHOOL_FEES, isWaived } from '../../../shared/constant/schoolFees';
import PublicLayout from '../../PublicDashboard/components/PublicLayout';
import QrScannerModal from './QrScannerModal';
import { maskFullName } from '../../../shared/utils/maskName';

const peso = (value) => `${Number(value) < 0 ? '-' : ''}₱${Math.abs(Number(value)).toLocaleString()}`;

function RecordsPage() {
  const [lrn, setLrn] = useState('');
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const { record, loading, error, searched, search } = useStudentRecord();

  const handleSubmit = (e) => {
    e.preventDefault();
    search(lrn.trim());
  };

  const handleScan = (scannedText) => {
    const digits = (scannedText.match(/\d/g) || []).join('').slice(0, 12);
    setIsScannerOpen(false);
    if (digits.length === 12) {
      setLrn(digits);
      search(digits);
    }
  };

  const total = record?.payments?.reduce((sum, p) => sum + Number(p.amount), 0) ?? 0;
  // Fully paid means every fee has a line. A waived PTA line (₱0) still counts as covered.
  const isFullyPaid =
    (record?.payments?.length ?? 0) > 0 &&
    SCHOOL_FEES.every((fee) => record.payments.some((p) => p.category === fee.category));

  return (
    <PublicLayout>
      <div className="w-full space-y-4">
      <section className="w-full bg-white rounded-lg border border-gray-200 p-4">
        <p className="-mx-4 -mt-4 mb-3 rounded-t-lg bg-[#3f6d8e] px-4 py-2 text-md lg:text-lg font-semibold text-white tracking-wide">
          Payment Records Lookup
        </p>
        <form onSubmit={handleSubmit} className="flex flex-wrap gap-3">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              required
              value={lrn}
              onChange={(e) => setLrn(e.target.value)}
              pattern="[0-9]{12}"
              maxLength={12}
              title="LRN must be exactly 12 digits"
              placeholder="Enter 12-digit LRN"
              className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2 text-lg focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto rounded-md bg-[#3f6d8e] text-white text-lg font-medium px-5 py-2 hover:bg-[#122436] disabled:opacity-50"
          >
            {loading ? 'Searching…' : 'Search'}
          </button>

          <button
            type="button"
            onClick={() => setIsScannerOpen(true)}
            title="Scan LRN QR code"
            className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-md border border-gray-300 text-lg font-medium px-5 py-2 text-gray-700 hover:bg-gray-50"
          >
            <QrCodeIcon className="h-5 w-5" />
            Scan ID Card QR
          </button>
        </form>
        <p className="text-xs text-gray-500 mt-2">
          Enter or Scan your child's LRN (Learner Reference Number) to view their SPTA payment records.
        </p>
      </section>

      {record && isFullyPaid && (
        <section className="w-full bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <p className="text-sm font-medium text-green-700">
            You have fully paid all SPTA fees for this school year.
          </p>
        </section>
      )}

      {error && (
        <section className="w-full bg-white rounded-lg border border-red-200 p-4">
          <p className="text-sm text-red-600">{error}</p>
        </section>
      )}

      {!error && searched && !loading && !record && (
        <section className="w-full bg-white rounded-lg border border-gray-200 p-8 text-center">
          <p className="text-sm text-gray-500">No record found for that LRN.</p>
        </section>
      )}

      {record && (
        <section className="w-full bg-white rounded-lg border border-gray-200 p-4">
          <p className="-mx-4 -mt-4 mb-3 rounded-t-lg bg-[#3f6d8e] px-4 py-2 text-md lg:text-lg font-semibold text-[#f2ecdb]">
            PAYMENT BREAKDOWN
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-2 text-sm mb-4">
            <p className="text-gray-500">Name</p>
            <p className="font-medium text-gray-900 sm:col-span-3">
              {maskFullName(record.student.firstname, record.student.lastname)}
            </p>
            <p className="text-gray-500">Grade & Section</p>
            <p className="font-medium text-gray-900 sm:col-span-3">
              {record.student.grade_level} {record.student.section ? `- ${record.student.section}` : ''}
            </p>
          </div>

          {record.payments.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No payments recorded yet.</p>
          ) : (
            <>
              <div className="hidden sm:block -mx-4 overflow-x-auto">
                <table className="w-full text-sm text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 text-md text-navy uppercase">
                      <th className="py-2 pl-4 pr-4 font-semibold whitespace-nowrap">Category</th>
                      <th className="py-2 pr-4 font-semibold whitespace-nowrap">Amount</th>
                      <th className="py-2 pr-4 font-semibold whitespace-nowrap">Date</th>
                      <th className="py-2 pr-4 font-semibold whitespace-nowrap">OR #</th>
                      <th className="py-2 pr-4 font-semibold whitespace-nowrap">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {record.payments.map((p, index) => (
                      <tr key={index} className="hover:bg-gold/10 active:bg-gold/20 transition-colors">
                        <td className="py-3 pl-4 pr-4 font-medium text-gray-900 whitespace-nowrap">{isWaived(p) ? 'PTA (waived)' : p.category}</td>
                        <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{peso(p.amount)}</td>
                        <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{p.payment_date}</td>
                        <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{p.or_number || '—'}</td>
                        <td className="py-3 pr-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              isFullyPaid ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning-dark'
                            }`}
                          >
                            {isFullyPaid ? 'Full' : 'Partial'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="sm:hidden space-y-3">
                {record.payments.map((p, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-gray-900">{isWaived(p) ? 'PTA (waived)' : p.category}</p>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          isFullyPaid ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning-dark'
                        }`}
                      >
                        {isFullyPaid ? 'Full' : 'Partial'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-y-1 text-sm">
                      <p className="text-gray-500">Amount</p>
                      <p className="text-right font-bold text-gray-700">{peso(p.amount)}</p>
                      <p className="text-gray-500">Date</p>
                      <p className="text-right font-bold text-gray-700">{p.payment_date}</p>
                      <p className="text-gray-500">OR #</p>
                      <p className="text-right font-bold text-gray-700">{p.or_number || '—'}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div
                className={`flex justify-between items-center mt-4 px-4 py-3 rounded-lg text-xl sm:text-2xl font-bold ${
                  isFullyPaid ? 'bg-success/10 text-success' : 'bg-gold/10 text-navy'
                }`}
              >
                <span>Total Paid</span>
                <span>{peso(total)}</span>
              </div>
            </>
          )}
        </section>
      )}
      </div>

      {isScannerOpen && (
        <QrScannerModal onClose={() => setIsScannerOpen(false)} onScan={handleScan} />
      )}
    </PublicLayout>
  );
}

export default RecordsPage;
